import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import TextEditor from "../../common/TextEditor/TextEditor";
import ConfirmModal from "../../common/ConfirmModal";
import QmsModalPortal from "../qms/QmsModalPortal";
import ScheduleDocumentUploadModal from "./ScheduleDocumentUploadModal";
import ScheduleWorksheetPreviewModal from "./ScheduleWorksheetPreviewModal";
import ScheduleEcommMergeTagsPanel from "./ScheduleEcommMergeTagsPanel";
import { getEcommSuggestions } from "./scheduleEcommSuggestions";
import {
  sanitizeEcommEmailHtmlForPaste,
  validateEcommEmailHtml,
} from "./scheduleEcommEmailContent";
import {
  FeatureOffNote,
  MiniField,
  NumberBox,
  StaticSetupSection,
  VisitColumnAccordion,
  formatCurrencyFieldLabel,
} from "./scheduleVisitPanelUi";
import { getSetupToggle } from "./scheduleWorkflow";
import { VISIT_TYPE, isAdhocVisit, resolveScheduleGroupId } from "./scheduleVisitDraft";
import {
  COMM_TEMPLATE_LABELS,
  COMM_TEMPLATE_TYPES,
  ORG_SMS_UNAVAILABLE_REASON,
  ORG_WHATSAPP_UNAVAILABLE_REASON,
  applyOrgChannelAvailability,
  emptyCommTemplates,
  fetchAdhocSetup,
  fetchAdhocWorksheets,
  fetchEcommTemplates,
  fetchOrganisationChannelFlags,
  fetchStudyWorksheets,
  fetchVisitPlannerDetails,
  mergePlannerRows,
  saveAdhocCost,
  saveAdhocEcommTemplates,
  saveAdhocPayment,
  saveAdhocWorksheet,
  saveEcommTemplates,
  saveStudyWorksheet,
  deleteStudyWorksheet,
  saveVisitCost,
  saveVisitPayment,
  saveVisitPlannerSetup,
  readPlannerSlotFields,
  readPlannerFinancialFields,
  VISIT_SLOT_DURATION_OPTIONS,
} from "./scheduleOptionalSetupService";
import {
  buildWorksheetPreviewModel,
  getWorksheetFileIconClass,
  getWorksheetUploadInfo,
  isDuplicateWorksheetName,
} from "./scheduleWorksheetUtils";
import {
  SCHEDULE_AUDIT_COLUMNS,
  ecommTemplateHasContent,
  formatCostAudit,
  formatDocumentAudit,
  formatPaymentAudit,
  formatPlannerSetup,
} from "./scheduleAuditEvents";
import { SCHEDULE_LIVE_NO_DOC_DELETE_MESSAGE } from "./scheduleSaveGate";

function freezeSavedWhenLive({ archived, live, saved }) {
  return !!archived || (!!live && !!saved);
}

async function emitScheduleAudit(onAuditEvent, payload, studyScheduleId = null, onError = null) {
  if (!onAuditEvent) {
    onError?.("Activity log is not available for this action.");
    return false;
  }
  try {
    await onAuditEvent({
      ...payload,
      studyScheduleId: payload.studyScheduleId || studyScheduleId || null,
    });
    return true;
  } catch (err) {
    const message = err?.message || "Could not record this action in the activity log.";
    console.warn("Schedule activity log write failed:", message);
    onError?.(message);
    return false;
  }
}

function SectionShell({ title, children, onSave, saving, saveLabel = "Save", compact = false, readOnly = false }) {
  return (
    <section className={`gvisit-legacy-section${compact ? " gvisit-legacy-section--compact" : ""}`}>
      <div className={`gvisit-optional__head${compact ? " gvisit-optional__head--compact" : ""}`}>
        <h4 className="gvisit-legacy-section__title">{title}</h4>
        {onSave && !readOnly ? (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={saving}
            onClick={onSave}
          >
            {saving ? "Saving…" : saveLabel}
          </button>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function VisitPlannerSetupSection({
  ctx,
  planner,
  visitName,
  needsApply,
  onSaved,
  onAuditEvent,
  onError,
  onReload,
  onPlannerPatch,
  readOnly = false,
}) {
  const [bookingPerSlot, setBookingPerSlot] = useState("");
  const [slotDuration, setSlotDuration] = useState("");
  const [saving, setSaving] = useState(false);
  const saved = readPlannerSlotFields(planner);

  useEffect(() => {
    setBookingPerSlot(saved.bookingPerSlot);
    setSlotDuration(saved.slotDuration);
  }, [saved.bookingPerSlot, saved.slotDuration, planner]);

  const save = async () => {
    if (readOnly) return;
    if (!ctx.visitId) {
      onError?.("This visit is not in the database yet.");
      return;
    }
    setSaving(true);
    try {
      await saveVisitPlannerSetup({
        ...ctx,
        bookingPerSlot,
        slotDuration,
      });
      const visitData = await onReload?.();
      if (!visitData?.planner) {
        onPlannerPatch?.({
          NoOfbookingPerSlot: bookingPerSlot,
          NoOfBookingPerSlot: bookingPerSlot,
          SlotDuration: slotDuration,
        });
      }
      onSaved?.("Planner setup saved.");
      await emitScheduleAudit(onAuditEvent, {
        actionLabel: saved.hasSaved ? "Planner setup updated" : "Planner setup saved",
        oldValue: formatPlannerSetup({
          bookingPerSlot: saved.bookingPerSlot,
          slotDuration: saved.slotDuration,
          visitName,
        }),
        newValue: formatPlannerSetup({ bookingPerSlot, slotDuration, visitName }),
        comments: visitName ? `Visit: ${visitName}` : "",
        columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
      }, ctx.studyScheduleId);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (needsApply) return null;

  return (
    <StaticSetupSection
      title="Planner setup"
      onSave={readOnly ? null : save}
      saving={saving}
      saveLabel={saved.hasSaved ? "Update" : "Save"}
      readOnly={readOnly}
    >
      <div className="gvisit-mini-grid gvisit-mini-grid--2 gvisit-mini-grid--planner">
        <MiniField label="Booking per slot" required>
          <NumberBox
            value={bookingPerSlot}
            maxLength={2}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={setBookingPerSlot}
          />
        </MiniField>
        <MiniField label="Slot duration (min)" required>
          <select
            className="gvisit-select-box"
            value={slotDuration}
            disabled={readOnly}
            onChange={(e) => setSlotDuration(e.target.value)}
          >
            <option value="">Select slot</option>
            {VISIT_SLOT_DURATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </MiniField>
      </div>
    </StaticSetupSection>
  );
}

function VisitPaymentSection({
  ctx,
  isAdhoc,
  showPayment,
  visitName,
  onSaved,
  onAuditEvent,
  onError,
  onReload,
  onPlannerPatch,
  readOnly = false,
}) {
  const [payment, setPayment] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const currency = ctx.currencySymbol || "£";

  useEffect(() => {
    if (isAdhoc) {
      setPayment(ctx.adhocPayment ?? "");
      setHasSaved(String(ctx.adhocPayment ?? "").trim() !== "");
      return;
    }
    const financial = readPlannerFinancialFields(ctx.planner);
    setPayment(financial.payment);
    setHasSaved(financial.hasSavedPayment);
  }, [ctx.adhocPayment, ctx.planner, ctx.visitId, isAdhoc]);

  const save = async () => {
    if (readOnly) return;
    if (!isAdhoc && !ctx.visitId) {
      onError?.("This visit is not in the database yet.");
      return;
    }
    setSaving(true);
    try {
      const oldPayment = isAdhoc
        ? String(ctx.adhocPayment ?? "")
        : readPlannerFinancialFields(ctx.planner).payment;
      if (isAdhoc) {
        await saveAdhocPayment({ ...ctx, payment });
      } else {
        await saveVisitPayment({ ...ctx, payment });
        await onReload?.();
        onPlannerPatch?.({ VisitPayment: payment, Payment: payment });
      }
      setHasSaved(true);
      onSaved?.("Payment saved.");
      await emitScheduleAudit(onAuditEvent, {
        actionLabel: hasSaved ? "Visit payment updated" : "Visit payment saved",
        oldValue: hasSaved ? formatPaymentAudit({ payment: oldPayment, currency }) : "",
        newValue: formatPaymentAudit({ payment, currency }),
        comments: visitName ? `Visit: ${visitName}` : "",
        columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
      }, ctx.studyScheduleId);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!showPayment) return null;

  return (
    <StaticSetupSection
      title="Payment setup"
      onSave={readOnly ? null : save}
      saving={saving}
      saveLabel={hasSaved ? "Update" : "Save"}
      readOnly={readOnly}
    >
      <MiniField label={formatCurrencyFieldLabel("Payment", currency)} required>
        <NumberBox
          value={payment}
          maxLength={11}
          allowDecimal
          maxDecimalPlaces={2}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={setPayment}
        />
      </MiniField>
    </StaticSetupSection>
  );
}

function VisitCostSection({
  ctx,
  isScreening,
  isAdhoc,
  showRandomCost,
  showCost,
  visitName,
  onSaved,
  onAuditEvent,
  onError,
  onReload,
  onPlannerPatch,
  readOnly = false,
}) {
  const [visitCost, setVisitCost] = useState("");
  const [randomCost, setRandomCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const currency = ctx.currencySymbol || "£";

  useEffect(() => {
    if (isAdhoc) {
      setVisitCost(ctx.adhocVisitCost ?? "");
      setRandomCost("");
      setHasSaved(String(ctx.adhocVisitCost ?? "").trim() !== "");
      return;
    }
    const financial = readPlannerFinancialFields(ctx.planner);
    setVisitCost(financial.visitCost);
    setRandomCost(financial.randomCost);
    setHasSaved(
      financial.hasSavedVisitCost ||
        (showRandomCost && financial.hasSavedRandomCost),
    );
  }, [ctx.adhocVisitCost, ctx.planner, ctx.visitId, isAdhoc, showRandomCost]);

  const save = async () => {
    if (readOnly) return;
    if (!isAdhoc && !ctx.visitId) {
      onError?.("This visit is not in the database yet.");
      return;
    }
    setSaving(true);
    try {
      const oldFinancial = isAdhoc
        ? { visitCost: String(ctx.adhocVisitCost ?? ""), randomCost: "" }
        : readPlannerFinancialFields(ctx.planner);
      if (isAdhoc) {
        await saveAdhocCost({ ...ctx, visitCost });
      } else {
        await saveVisitCost({
          ...ctx,
          visitCost,
          randomCost: showRandomCost ? randomCost : "",
        });
        const savedPatch = {
          VisitCost: visitCost,
          ...(showRandomCost ? { RandomCost: randomCost } : {}),
        };
        await onReload?.();
        onPlannerPatch?.(savedPatch);
      }
      setHasSaved(true);
      onSaved?.("Cost saved.");
      await emitScheduleAudit(onAuditEvent, {
        actionLabel: hasSaved ? "Visit cost updated" : "Visit cost saved",
        oldValue: hasSaved
          ? formatCostAudit({
              visitCost: oldFinancial.visitCost,
              randomCost: oldFinancial.randomCost,
              currency,
              isScreening,
            })
          : "",
        newValue: formatCostAudit({
          visitCost,
          randomCost: showRandomCost ? randomCost : "",
          currency,
          isScreening,
        }),
        comments: visitName ? `Visit: ${visitName}` : "",
        columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
      }, ctx.studyScheduleId);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!showCost) return null;

  const primaryCostLabel = isScreening
    ? formatCurrencyFieldLabel("Screening", currency)
    : formatCurrencyFieldLabel("Visit cost", currency);

  return (
    <StaticSetupSection
      title="Cost setup"
      onSave={readOnly ? null : save}
      saving={saving}
      saveLabel={hasSaved ? "Update" : "Save"}
      readOnly={readOnly}
    >
      <div
        className={`gvisit-mini-grid gvisit-mini-grid--cost${
          showRandomCost ? "" : " gvisit-mini-grid--cost-single"
        }`}
      >
        <MiniField label={primaryCostLabel} required>
          <NumberBox
            value={visitCost}
            maxLength={11}
            allowDecimal
            maxDecimalPlaces={2}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={setVisitCost}
          />
        </MiniField>
        {showRandomCost ? (
          <MiniField label={formatCurrencyFieldLabel("Random", currency)} required>
            <NumberBox
              value={randomCost}
              maxLength={11}
              allowDecimal
              maxDecimalPlaces={2}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={setRandomCost}
            />
          </MiniField>
        ) : null}
      </div>
    </StaticSetupSection>
  );
}

function appendMergeTag(currentValue, tag) {
  const base = String(currentValue || "");
  const spacer = base && !/\s$/.test(base) ? " " : "";
  return `${base}${spacer}${tag}`;
}

function plainTextToEmailHtml(text) {
  const lines = String(text || "").split(/\r?\n/);
  return lines
    .map((line) => {
      const escaped = line
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return `<p>${escaped || "<br>"}</p>`;
    })
    .join("");
}

function commTemplateFingerprint(template) {
  if (!template) return "";
  return JSON.stringify({
    TemplateType: template.TemplateType || "",
    IsEmail: !!template.IsEmail,
    IsSMS: !!template.IsSMS,
    IsWhatsApp: !!template.IsWhatsApp,
    EmailSubject: String(template.EmailSubject || "").trim(),
    EmailText: String(template.EmailText || "").trim(),
    SMSText: String(template.SMSText || "").trim(),
  });
}

function isCommTemplateDirty(current, saved) {
  if (!current) return false;
  if (!saved || saved.TemplateType !== current.TemplateType) return false;
  return commTemplateFingerprint(current) !== commTemplateFingerprint(saved);
}

function SuggestedFillControl({ disabled, onFill }) {
  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await onFill?.();
  };

  return (
    <button
      type="button"
      className="gvisit-comm-inline-ai"
      disabled={disabled}
      onClick={handleClick}
      title="Fill this template with an AI suggestion. You can edit it after."
    >
      <i className="fas fa-wand-magic-sparkles" aria-hidden />
      <span>Fill with AI suggestion</span>
    </button>
  );
}

function CommChannelBlock({
  title,
  enabled,
  onEnabledChange,
  readOnly = false,
  orgAvailable = true,
  unavailableReason = "",
  className = "",
  extraAction = null,
  children,
}) {
  const showEnabled = orgAvailable && enabled;
  return (
    <section
      className={`gvisit-fold gvisit-fold--dashed gvisit-comm-fold is-open${
        !showEnabled ? " is-disabled" : ""
      }${!orgAvailable ? " is-org-unavailable" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="gvisit-comm-fold__head">
        <span className="gvisit-comm-fold__title">{title}</span>
        <div className="gvisit-comm-fold__actions">
          {extraAction}
          <label className="gvisit-comm-channel-toggle form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              checked={!!showEnabled}
              disabled={readOnly || !orgAvailable}
              onChange={(e) => onEnabledChange?.(e.target.checked)}
              aria-label={`${title} channel`}
            />
          </label>
        </div>
      </div>
      <div className="gvisit-comm-fold__body">
        {orgAvailable ? (
          children
        ) : (
          <div className="gvisit-comm-org-unavailable" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <p>{unavailableReason}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function CommTemplateCard({
  template,
  onChange,
  compact = false,
  expanded = false,
  onFieldFocus,
  onEmailContentBlocked = null,
  readOnly = false,
  orgSmsEnabled = false,
  orgWhatsAppEnabled = false,
  orgChannelsLoaded = false,
  onFillChannel = null,
}) {
  if (!template) return null;

  const setField = (field, value) => {
    if (readOnly) return;
    onChange({ ...template, [field]: value });
  };

  const bodyFieldHeight = compact ? "100%" : "6.25rem";
  const editorHeight = expanded || compact ? "100%" : bodyFieldHeight;
  const editorMinHeight = expanded ? "4rem" : compact ? "12rem" : bodyFieldHeight;

  return (
    <div
      className={`gvisit-comm-card${compact ? " gvisit-comm-card--compact" : ""}${
        expanded ? " gvisit-comm-card--expanded" : ""
      }`}
    >
      <div className="gvisit-comm-channels gvisit-comm-channels--3col">
        <CommChannelBlock
          title="Email"
          enabled={!!template.IsEmail}
          onEnabledChange={(checked) => setField("IsEmail", checked)}
          readOnly={readOnly}
          className="gvisit-comm-fold--email"
        >
          <label className="gvisit-legacy-field gvisit-legacy-field--compact gvisit-comm-subject-field">
            <span className="gvisit-legacy-field__label">
              Email Subject
              {!readOnly && onFillChannel ? (
                <SuggestedFillControl onFill={() => onFillChannel("email")} />
              ) : null}
            </span>
            <input
              type="text"
              className="form-control gvisit-comm-subject-input"
              value={template.EmailSubject}
              disabled={readOnly || !template.IsEmail}
              readOnly={readOnly}
              placeholder="Enter email subject"
              onFocus={() => onFieldFocus?.("subject")}
              onChange={(e) => setField("EmailSubject", e.target.value)}
            />
          </label>
          <div className="gvisit-legacy-field gvisit-legacy-field--compact gvisit-comm-body-field">
            <span className="gvisit-legacy-field__label" id="gvisit-comm-email-body-label">
              Email body
            </span>
            <div
              className={`gvisit-comm-email-editor gvisit-ecomm-editor${
                !template.IsEmail ? " is-disabled" : ""
              }`}
              role="group"
              aria-labelledby="gvisit-comm-email-body-label"
              onFocusCapture={() => onFieldFocus?.("email")}
            >
              <TextEditor
                value={template.EmailText || ""}
                onChange={(html) => setField("EmailText", html)}
                placeholder="Design your email…"
                height={editorHeight}
                minHeight={editorMinHeight}
                toolbarVariant="full"
                showImageButton={false}
                showLinkButton={false}
                showCodeBlock={false}
                showCleanButton={false}
                blockImages
                sanitizeHtml
                sanitizePasteHtml={sanitizeEcommEmailHtmlForPaste}
                validateContentBeforeApply={validateEcommEmailHtml}
                onContentBlocked={onEmailContentBlocked}
                className="gvisit-ecomm-quill"
                disabled={readOnly || !template.IsEmail}
              />
            </div>
          </div>
        </CommChannelBlock>

        <CommChannelBlock
          title="SMS"
          enabled={!!template.IsSMS}
          onEnabledChange={(checked) => setField("IsSMS", checked)}
          readOnly={readOnly}
          orgAvailable={orgSmsEnabled}
          unavailableReason={
            orgChannelsLoaded && !orgSmsEnabled ? ORG_SMS_UNAVAILABLE_REASON : ""
          }
          className="gvisit-comm-fold--sms"
        >
          <label className="gvisit-legacy-field gvisit-legacy-field--compact gvisit-comm-sms-field">
            <span className="gvisit-legacy-field__label">
              <span className="gvisit-comm-sms-label-left">
                SMS text (max 160)
                {!readOnly && orgSmsEnabled && onFillChannel ? (
                  <SuggestedFillControl onFill={() => onFillChannel("sms")} />
                ) : null}
              </span>
              <span className="gvisit-comm-sms-count">
                {String(template.SMSText || "").length}/160
              </span>
            </span>
            <textarea
              className="form-control form-control-sm gvisit-comm-sms-input"
              maxLength={160}
              value={template.SMSText}
              disabled={readOnly || !template.IsSMS}
              readOnly={readOnly}
              onFocus={() => onFieldFocus?.("sms")}
              onChange={(e) => setField("SMSText", e.target.value)}
            />
          </label>
        </CommChannelBlock>

        <CommChannelBlock
          title="WhatsApp"
          enabled={!!template.IsWhatsApp}
          onEnabledChange={(checked) => setField("IsWhatsApp", checked)}
          readOnly={readOnly}
          orgAvailable={orgWhatsAppEnabled}
          unavailableReason={
            orgChannelsLoaded && !orgWhatsAppEnabled
              ? ORG_WHATSAPP_UNAVAILABLE_REASON
              : ""
          }
          className="gvisit-comm-fold--whatsapp"
        >
          <label className="gvisit-legacy-field gvisit-legacy-field--compact gvisit-comm-sms-field">
            <span className="gvisit-legacy-field__label">
              Approved WhatsApp template
              <span className="gvisit-comm-locked-badge">
                <i className="fas fa-lock" aria-hidden />
                You cannot change this
              </span>
            </span>
            <div className="gvisit-comm-scroll gvisit-comm-scroll--locked" tabIndex={0}>
              <pre className="gvisit-comm-whatsapp-body">
                {template.WhatsAppText || "No approved WhatsApp template is available."}
              </pre>
            </div>
            <span className="gvisit-comm-whatsapp-notes">
              This approved template is read-only. Variables are filled when the
              message is sent.
            </span>
          </label>
        </CommChannelBlock>
      </div>
    </div>
  );
}

function EcommunicationPanelContent({
  templates,
  onTemplatesChange,
  activeType,
  onActiveTypeChange,
  compact = false,
  expanded = false,
  onFieldFocus,
  onEmailContentBlocked = null,
  onInsertTag = null,
  insertTarget = "email",
  insertTargetLabel = "Email body",
  onFillChannel = null,
  readOnly = false,
  orgSmsEnabled = false,
  orgWhatsAppEnabled = false,
  orgChannelsLoaded = false,
}) {
  const [railFlyout, setRailFlyout] = useState(null);
  const active =
    templates.find((t) => t.TemplateType === activeType) || templates[0];

  const handleFieldFocus = useCallback(
    (target) => {
      onFieldFocus?.(target);
      setRailFlyout(null);
    },
    [onFieldFocus],
  );

  return (
    <div className="gvisit-comm-panel__body">
      <div className="gvisit-comm-layout">
        <nav
          className={`gvisit-comm-layout__rail${
            railFlyout ? " has-flyout-open" : ""
          }`}
          aria-label="Communication template types"
        >
          <div className="gvisit-comm-tabs gvisit-comm-tabs--vertical gvisit-comm-tabs--compact">
            {COMM_TEMPLATE_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`gvisit-comm-tabs__btn${
                  activeType === type ? " is-active" : ""
                }`}
                onClick={() => onActiveTypeChange(type)}
                title={COMM_TEMPLATE_LABELS[type] || type}
              >
                {COMM_TEMPLATE_LABELS[type] || type}
              </button>
            ))}
          </div>
          {!readOnly ? (
            <>
          <ScheduleEcommMergeTagsPanel
            variant="popover"
            compact
            toggleMode="click"
            placement="rail"
            open={railFlyout === "insert"}
            onOpenChange={(next) => setRailFlyout(next ? "insert" : null)}
            onInsertTag={onInsertTag}
            insertTargetLabel={insertTargetLabel}
            onActivate={() => {
              if (insertTarget === "subject") onFieldFocus?.("subject");
              else if (insertTarget === "sms") onFieldFocus?.("sms");
              else if (insertTarget === "whatsapp") onFieldFocus?.("whatsapp");
              else onFieldFocus?.("email");
            }}
          />
            </>
          ) : null}
        </nav>
        <div className="gvisit-comm-layout__main">
          <CommTemplateCard
            template={active}
            compact={compact}
            expanded={expanded}
            orgSmsEnabled={orgSmsEnabled}
            orgWhatsAppEnabled={orgWhatsAppEnabled}
            orgChannelsLoaded={orgChannelsLoaded}
            onFieldFocus={handleFieldFocus}
            onEmailContentBlocked={onEmailContentBlocked}
            onFillChannel={readOnly ? null : onFillChannel}
            readOnly={readOnly}
            onChange={(next) =>
              onTemplatesChange(
                templates.map((t) =>
                  t.TemplateType === next.TemplateType ? next : t,
                ),
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

function EcommunicationSection({
  ctx,
  isAdhoc,
  compact = false,
  visitName,
  onSaved,
  onAuditEvent,
  onError,
  readOnly = false,
  showInlineExpand = true,
  expandRef = null,
}) {
  const [templates, setTemplates] = useState(emptyCommTemplates());
  const [initialActiveTemplate, setInitialActiveTemplate] = useState(null);
  const [activeType, setActiveType] = useState(COMM_TEMPLATE_TYPES[0]);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [insertTarget, setInsertTarget] = useState("email");
  const [pendingType, setPendingType] = useState(null);
  const [orgChannels, setOrgChannels] = useState({
    smsEnabled: false,
    whatsAppEnabled: false,
    loaded: false,
  });

  useImperativeHandle(expandRef, () => ({
    open: () => setModalOpen(true),
  }));

  useEffect(() => {
    if (!ctx.studyScheduleId) return;
    if (!isAdhoc && !ctx.visitId) return;
    let cancelled = false;
    (async () => {
      try {
        const [data, channels] = await Promise.all([
          isAdhoc ? fetchAdhocSetup(ctx) : fetchEcommTemplates(ctx),
          fetchOrganisationChannelFlags(ctx.organizationId),
        ]);
        const gated = applyOrgChannelAvailability(data.templates, channels);
        if (!cancelled) setOrgChannels({ ...channels, loaded: true });
        if (!cancelled) setTemplates(gated);
        if (!cancelled) {
          const activeTemplate =
            gated.find((t) => t.TemplateType === activeType) || gated[0];
          setInitialActiveTemplate(activeTemplate ? { ...activeTemplate } : null);
        }
      } catch {
        if (!cancelled) {
          setOrgChannels({ smsEnabled: false, whatsAppEnabled: false, loaded: true });
          setTemplates(emptyCommTemplates());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.visitId, ctx.studyScheduleId, ctx.groupId, ctx.organizationId, isAdhoc]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const root = document.documentElement;
    root.classList.add("gvisit-comm-modal-open");
    const prevHtmlOverflow = root.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    root.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");
    return () => {
      root.classList.remove("gvisit-comm-modal-open");
      root.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.classList.remove("modal-open");
    };
  }, [modalOpen]);

  const active =
    templates.find((t) => t.TemplateType === activeType) || templates[0];

  useEffect(() => {
    if (!active) return;
    setInitialActiveTemplate((prev) => {
      if (prev?.TemplateType === active.TemplateType) return prev;
      return { ...active };
    });
  }, [activeType, active]);

  const discardCurrentTemplate = useCallback(() => {
    if (!initialActiveTemplate) return;
    setTemplates((prev) =>
      prev.map((t) =>
        t.TemplateType === initialActiveTemplate.TemplateType
          ? { ...initialActiveTemplate }
          : t,
      ),
    );
  }, [initialActiveTemplate]);

  const handleSelectTemplate = useCallback(
    (nextType) => {
      if (nextType === activeType) return;
      if (isCommTemplateDirty(active, initialActiveTemplate)) {
        setPendingType(nextType);
        return;
      }
      setActiveType(nextType);
    },
    [active, activeType, initialActiveTemplate],
  );

  const updateActiveTemplate = useCallback((next) => {
    setTemplates((prev) =>
      prev.map((t) => (t.TemplateType === next.TemplateType ? next : t)),
    );
  }, []);

  const insertTag = useCallback(
    (tag) => {
      if (!active) return;
      if (insertTarget === "subject" && active.IsEmail) {
        updateActiveTemplate({
          ...active,
          EmailSubject: appendMergeTag(active.EmailSubject, tag),
        });
        return;
      }
      if (insertTarget === "sms" && orgChannels.smsEnabled && active.IsSMS) {
        updateActiveTemplate({
          ...active,
          SMSText: appendMergeTag(active.SMSText, tag),
        });
        return;
      }
      if (insertTarget === "whatsapp" && orgChannels.whatsAppEnabled && active.IsWhatsApp) {
        updateActiveTemplate({
          ...active,
          WhatsAppText: appendMergeTag(active.WhatsAppText, tag),
        });
        return;
      }
      if (active.IsEmail) {
        updateActiveTemplate({
          ...active,
          EmailText: appendMergeTag(active.EmailText, tag),
        });
      } else if (orgChannels.smsEnabled && active.IsSMS) {
        updateActiveTemplate({
          ...active,
          SMSText: appendMergeTag(active.SMSText, tag),
        });
      } else if (orgChannels.whatsAppEnabled && active.IsWhatsApp) {
        updateActiveTemplate({
          ...active,
          WhatsAppText: appendMergeTag(active.WhatsAppText, tag),
        });
      }
    },
    [active, insertTarget, orgChannels.smsEnabled, orgChannels.whatsAppEnabled, updateActiveTemplate],
  );

  const applyChannelSuggestion = useCallback(
    async (channel) => {
      if (!active) return false;
      const suggestion = getEcommSuggestions(active.TemplateType || activeType);

      if (channel === "email") {
        const subject = String(suggestion.emailSubject || "").trim();
        const body = String(suggestion.emailBody || "").trim();
        if (!subject && !body) return false;
        updateActiveTemplate({
          ...active,
          IsEmail: true,
          EmailSubject: subject || active.EmailSubject,
          EmailText: body ? plainTextToEmailHtml(body) : active.EmailText,
        });
        return true;
      }

      if (channel === "sms") {
        if (!orgChannels.smsEnabled) return false;
        const text = String(suggestion.smsText || "").trim().slice(0, 160);
        if (!text) return false;
        updateActiveTemplate({
          ...active,
          IsSMS: true,
          SMSText: text,
        });
        return true;
      }

      return false;
    },
    [active, activeType, orgChannels.smsEnabled, updateActiveTemplate],
  );

  const save = async () => {
    if (!isAdhoc && !ctx.visitId) {
      onError?.("This visit is not in the database yet.");
      return false;
    }
    setSaving(true);
    try {
      const gatedTemplates = applyOrgChannelAvailability(templates, orgChannels);
      const auditArgs = {
        ...ctx,
        templates: gatedTemplates,
        activeTemplateType: activeType,
        visitName,
        beforeTemplate: initialActiveTemplate,
        isTemplateUpdate: ecommTemplateHasContent(initialActiveTemplate),
        onAuditWritten: ctx.onAuditLogRefresh,
        orgSmsEnabled: orgChannels.smsEnabled,
        orgWhatsAppEnabled: orgChannels.whatsAppEnabled,
      };
      if (isAdhoc) {
        await saveAdhocEcommTemplates(auditArgs);
      } else {
        await saveEcommTemplates(auditArgs);
      }
      try {
        const data = isAdhoc ? await fetchAdhocSetup(ctx) : await fetchEcommTemplates(ctx);
        const reloaded = applyOrgChannelAvailability(data.templates, orgChannels);
        setTemplates(reloaded);
        const reloadedActive =
          reloaded.find((t) => t.TemplateType === activeType) || reloaded[0];
        setInitialActiveTemplate(reloadedActive ? { ...reloadedActive } : { ...active });
      } catch {
        setInitialActiveTemplate({ ...active });
      }
      onSaved?.(
        isAdhoc ? "Adhoc e-communication saved." : "E-communication templates saved.",
      );
      return true;
    } catch (err) {
      onError?.(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmSaveAndLeave = async () => {
    const nextType = pendingType;
    const saved = await save();
    if (!saved) return;
    if (nextType) setActiveType(nextType);
    setPendingType(null);
  };

  const confirmDiscardAndLeave = () => {
    const nextType = pendingType;
    discardCurrentTemplate();
    if (nextType) setActiveType(nextType);
    setPendingType(null);
  };

  const insertTargetLabel =
    insertTarget === "subject"
      ? "Email Subject"
      : insertTarget === "sms"
        ? "SMS"
        : insertTarget === "whatsapp"
          ? "WhatsApp"
          : "Email body";

  const panelContentProps = {
    templates,
    onTemplatesChange: readOnly ? () => {} : setTemplates,
    activeType,
    onActiveTypeChange: handleSelectTemplate,
    compact,
    onInsertTag: readOnly ? null : insertTag,
    insertTarget,
    insertTargetLabel,
    onFillChannel: readOnly ? null : applyChannelSuggestion,
    onEmailContentBlocked: (message) => onError?.(message),
    readOnly,
    orgSmsEnabled: orgChannels.smsEnabled,
    orgWhatsAppEnabled: orgChannels.whatsAppEnabled,
    orgChannelsLoaded: orgChannels.loaded,
  };

  return (
    <>
      <div className="gvisit-comm-panel">
        {showInlineExpand ? (
        <div className="gvisit-comm-panel__head">
          <h4 className="gvisit-comm-panel__title">Communication</h4>
          <div className="gvisit-comm-panel__actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm gvisit-comm-expand-btn gvisit-comm-expand-btn--tab"
              title="Open in larger view"
              aria-label="Expand communication"
              onClick={() => setModalOpen(true)}
            >
              <i className="fas fa-expand" aria-hidden="true" />
            </button>
          </div>
        </div>
        ) : null}
        <EcommunicationPanelContent
          {...panelContentProps}
          onFieldFocus={setInsertTarget}
        />
        {!readOnly ? (
          <div className="gvisit-section-foot">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : null}
      </div>

      {modalOpen ? (
        <QmsModalPortal>
          <div
            className="modal fade show d-block gvisit-comm-workspace-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gvisit-comm-modal-title"
          >
            <div className="modal-dialog gvisit-comm-workspace-modal__dialog">
              <div className="modal-content gvisit-comm-workspace-modal__content">
                <div className="gvisit-comm-workspace-modal__header">
                  <h5 className="gvisit-comm-workspace-modal__title" id="gvisit-comm-modal-title">
                    Communication — {COMM_TEMPLATE_LABELS[activeType] || activeType}
                  </h5>
                  <div className="gvisit-comm-workspace-modal__header-actions" />
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setModalOpen(false)}
                  />
                </div>
                <div className="gvisit-comm-workspace-modal__body">
                  <EcommunicationPanelContent
                    {...panelContentProps}
                    compact={false}
                    expanded
                    onFieldFocus={setInsertTarget}
                  />
                </div>
                <div className="gvisit-comm-workspace-modal__footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setModalOpen(false)}
                  >
                    Close
                  </button>
                  {!readOnly ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      onClick={async () => {
                        await save();
                        setModalOpen(false);
                      }}
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </QmsModalPortal>
      ) : null}

      <ConfirmModal
        isOpen={!!pendingType}
        title="Unsaved changes"
        message="You have unsaved communication changes. Save them before continuing?"
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-primary"
        icon="fas fa-exclamation-triangle text-warning"
        onConfirm={confirmSaveAndLeave}
        onClose={confirmDiscardAndLeave}
      />
    </>
  );
}

function WorksheetDocIconButton({ label, iconClass, tone = "neutral", onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={`gvisit-doc-card__icon-btn is-${tone}`}
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      <i className={iconClass} aria-hidden="true" />
    </button>
  );
}

function WorksheetSection({
  ctx,
  isAdhoc,
  visitName,
  onSaved,
  onAuditEvent,
  onPromptAuditedVisitAction,
  onError,
  readOnly = false,
  allowDelete = true,
}) {
  const [existing, setExisting] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const reload = useCallback(async () => {
    if (!ctx.studyScheduleId) return [];
    if (!isAdhoc && !ctx.visitId) return [];
    try {
      const rows = isAdhoc
        ? await fetchAdhocWorksheets(ctx)
        : await fetchStudyWorksheets(ctx);
      const list = Array.isArray(rows) ? rows : [];
      setExisting(list);
      return list;
    } catch {
      setExisting([]);
      return [];
    }
  }, [ctx, isAdhoc]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openAddModal = () => {
    setModalMode("add");
    setReplaceTarget(null);
    setModalOpen(true);
  };

  const openReplaceModal = (row) => {
    const id = row.StudyWorkSheetId || row.studyWorkSheetId;
    const name = row.WorkSheetName || row.workSheetName || "Document";
    const version = row.VersionNumber || row.versionNumber || 1;
    const groupId = row.StudyWorkSheetGroupId || row.studyWorkSheetGroupId || null;
    setModalMode("replace");
    setReplaceTarget({
      studyWorkSheetId: id,
      studyWorkSheetGroupId: groupId,
      workSheetName: name,
      version,
    });
    setModalOpen(true);
  };

  const openPreviewModal = (row) => {
    const model = buildWorksheetPreviewModel(row);
    if (!model?.documentUrl) {
      onError?.("Document preview is not available for this file.");
      return;
    }
    setPreviewRow(row);
  };

  const closePreviewModal = () => setPreviewRow(null);

  const closeModal = () => {
    setModalOpen(false);
    setReplaceTarget(null);
    setModalMode("add");
  };

  const handleSaveDocument = async ({
    workSheetName,
    file,
    documentName,
    fileExtension,
    studyWorkSheetId,
  }) => {
    if (!isAdhoc && !ctx.visitId) {
      onError?.("This visit is not in the database yet.");
      return;
    }
    if (!isAdhoc && !ctx.groupId) {
      onError?.("Study group is missing. Save the schedule groups step first, then try again.");
      return;
    }
    if (
      isDuplicateWorksheetName(workSheetName, existing, {
        excludeStudyWorkSheetId: studyWorkSheetId || null,
        excludeStudyWorkSheetGroupId: replaceTarget?.studyWorkSheetGroupId || null,
      })
    ) {
      onError?.("A document with this name already exists for this visit.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...ctx,
        workSheetName,
        file,
        documentName,
        fileExtension,
        studyWorkSheetId: studyWorkSheetId || null,
      };
      if (isAdhoc) {
        await saveAdhocWorksheet(payload);
      } else {
        await saveStudyWorksheet(payload);
      }
      const rows = await reload();
      closeModal();
      const savedRow = rows.find(
        (row) =>
          String(row.WorkSheetName || row.workSheetName || "").trim().toLowerCase() ===
          workSheetName.trim().toLowerCase(),
      );
      const docUrl = savedRow?.WorkSheetURL || savedRow?.workSheetUrl || "";
      const docVersion = savedRow?.VersionNumber || savedRow?.versionNumber || 1;
      const isVersionUpload = Boolean(studyWorkSheetId);
      onSaved?.(
        isVersionUpload
          ? "Schedule document replaced with a new version."
          : "Schedule document saved.",
      );
      await emitScheduleAudit(onAuditEvent, {
        actionLabel: isVersionUpload
          ? "Schedule document updated"
          : "Schedule document uploaded",
        oldValue: isVersionUpload
          ? formatDocumentAudit({
              name: workSheetName,
              version: replaceTarget?.version || docVersion - 1,
              action: "uploaded",
            })
          : "",
        newValue: formatDocumentAudit({
          name: workSheetName,
          url: docUrl,
          version: docVersion,
          action: "uploaded",
        }),
        comments: visitName ? `Visit: ${visitName}` : "",
        columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
      }, ctx.studyScheduleId);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (row) => {
    if (!allowDelete) {
      onError?.(SCHEDULE_LIVE_NO_DOC_DELETE_MESSAGE);
      return;
    }
    const id = row.StudyWorkSheetId || row.studyWorkSheetId;
    const name = row.WorkSheetName || row.workSheetName || "Document";
    const version = row.VersionNumber || row.versionNumber || 1;
    const isLocked = !!(row.IsLocked ?? row.isLocked);

    if (!id) {
      onError?.("Document id is missing.");
      return;
    }
    if (isLocked) {
      onError?.("Locked documents cannot be removed.");
      return;
    }
    if (!onPromptAuditedVisitAction) {
      onError?.("Document removal is not available.");
      return;
    }

    await onPromptAuditedVisitAction({
      title: "Remove document",
      message: `Remove "${name}" from this visit? All versions will be deleted.`,
      confirmText: "Remove",
      confirmButtonClass: "btn-danger",
      onProceed: async (comments) => {
        setDeletingId(id);
        try {
          await deleteStudyWorksheet({
            studyWorkSheetId: id,
            organizationId: ctx.organizationId,
            createdBy: ctx.createdBy,
          });
          await reload();
          onSaved?.("Schedule document removed.");
          await emitScheduleAudit(onAuditEvent, {
            actionLabel: "Schedule document deleted",
            oldValue: formatDocumentAudit({
              name,
              version,
              action: "deleted",
            }),
            newValue: "",
            comments: [visitName ? `Visit: ${visitName}` : "", comments]
              .filter(Boolean)
              .join(" · "),
            columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
          }, ctx.studyScheduleId);
        } catch (err) {
          onError?.(err.message);
          throw err;
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  return (
    <div className="gvisit-docs-panel">
      <div className="gvisit-docs-panel__head">
        <h4 className="gvisit-docs-panel__title">Documents</h4>
        {!readOnly ? (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={openAddModal}
          >
            <i className="fas fa-plus me-1" aria-hidden />
            Add document
          </button>
        ) : null}
      </div>
      {existing.length ? (
        <ul className="gvisit-doc-cards">
          {existing.map((row) => {
            const id = row.StudyWorkSheetId || row.studyWorkSheetId;
            const name = row.WorkSheetName || row.workSheetName || "Document";
            const url = row.WorkSheetURL || row.workSheetUrl || "";
            const version = row.VersionNumber || row.versionNumber || 1;
            const isLocked = !!(row.IsLocked ?? row.isLocked);
            const uploadInfo = getWorksheetUploadInfo(row);
            const fileIconClass = getWorksheetFileIconClass(name, url);
            return (
              <li key={id || `${name}-${url}`} className="gvisit-doc-card">
                <div className="gvisit-doc-card__icon" aria-hidden>
                  <i className={fileIconClass} />
                </div>
                <div className="gvisit-doc-card__body">
                  <button
                    type="button"
                    className="gvisit-doc-card__name-btn"
                    title={uploadInfo.tooltip || "Preview document"}
                    onClick={() => openPreviewModal(row)}
                  >
                    {name}
                  </button>
                  <span
                    className="gvisit-doc-card__meta"
                    title={uploadInfo.tooltip || undefined}
                  >
                    v{version}
                    {uploadInfo.shortText ? ` · ${uploadInfo.shortText}` : ""}
                    {isLocked ? " · Locked" : ""}
                  </span>
                </div>
                <div className="gvisit-doc-card__actions">
                  {!readOnly && !isLocked ? (
                    <>
                      <WorksheetDocIconButton
                        label="Upload newer version"
                        iconClass="fas fa-upload"
                        tone="replace"
                        onClick={() => openReplaceModal(row)}
                      />
                      {allowDelete ? (
                        <WorksheetDocIconButton
                          label="Remove document"
                          iconClass="fas fa-trash-alt"
                          tone="remove"
                          disabled={deletingId === id}
                          onClick={() => handleDeleteDocument(row)}
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="gvisit-doc-empty">No documents yet. Add one for this visit.</p>
      )}
      <ScheduleDocumentUploadModal
        isOpen={modalOpen}
        mode={modalMode}
        replaceTarget={replaceTarget}
        existingRows={existing}
        saving={saving}
        onClose={closeModal}
        onSave={handleSaveDocument}
      />
      <ScheduleWorksheetPreviewModal
        open={Boolean(previewRow)}
        onClose={closePreviewModal}
        ctx={ctx}
        initialRow={previewRow}
        isAdhoc={isAdhoc}
      />
    </div>
  );
}

function VisitSetupAccordionGroup({
  plannerBody,
  commSection,
  commExpandButton,
  commToggle,
  worksheetSection,
  worksheetToggle,
}) {
  const [openPanel, setOpenPanel] = useState("setup");

  const setPanel = (key) => (nextOpen) => {
    setOpenPanel(nextOpen ? key : null);
  };

  return (
    <>
      <VisitColumnAccordion
        title="Setup"
        open={openPanel === "setup"}
        onOpenChange={setPanel("setup")}
      >
        {plannerBody}
      </VisitColumnAccordion>
      <VisitColumnAccordion
        title="Communication"
        className="gvisit-col-accordion--comm"
        open={openPanel === "communication"}
        onOpenChange={setPanel("communication")}
        headerActions={commExpandButton}
      >
        {commSection || (
          <FeatureOffNote
            title="E-communication"
            impact={commToggle?.offImpact}
          />
        )}
      </VisitColumnAccordion>
      <VisitColumnAccordion
        title="Documents"
        open={openPanel === "documents"}
        onOpenChange={setPanel("documents")}
      >
        {worksheetSection || (
          <FeatureOffNote
            title="Study worksheet"
            impact={worksheetToggle?.offImpact}
          />
        )}
      </VisitColumnAccordion>
    </>
  );
}

export default function ScheduleVisitOptionalSections({
  visit,
  setupFlags = {},
  studyScheduleId,
  trialStudyId = null,
  organizationId,
  createdBy,
  groupId = null,
  screeningRandomisationTogether = false,
  compact = false,
  layout = "stack",
  visitRailSlot = null,
  timePointSlot = null,
  readOnly = false,
  isScheduleLive = false,
  isStudyArchived = false,
  onNotify,
  onAuditEvent = null,
  onAuditLogRefresh = null,
  onReadinessRefresh = null,
  onPromptAuditedVisitAction = null,
  onVisitPlannerSnapshotPatch = null,
}) {
  const isAdhoc = isAdhocVisit(visit);
  const isScreening =
    !isAdhoc && (visit?.visitType === VISIT_TYPE.SCREENING || visit?.isScreening);
  const visitId = isAdhoc ? null : visit?.visitsPerStudyId || null;
  const visitName = String(visit?.visitName || "").trim();
  const visitPlannerSnapshot = visit?.plannerSnapshot ?? null;

  const [planner, setPlanner] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [adhocPayment, setAdhocPayment] = useState("");
  const [adhocVisitCost, setAdhocVisitCost] = useState("");
  const commExpandRef = useRef(null);

  const resolvedGroupId = resolveScheduleGroupId({ groupId, visit });

  const ctx = {
    studyScheduleId,
    trialStudyId: trialStudyId || studyScheduleId,
    visitId,
    organizationId,
    createdBy,
    groupId: resolvedGroupId,
    onAuditLogRefresh,
  };

  const patchPlanner = useCallback(
    (patch) => {
      setPlanner((prev) => mergePlannerRows(prev, patch));
      const visitRef = visit?.id || visit?.visitsPerStudyId;
      if (visitRef) {
        onVisitPlannerSnapshotPatch?.(visitRef, patch);
      }
    },
    [onVisitPlannerSnapshotPatch, visit?.id, visit?.visitsPerStudyId],
  );

  const reloadPlanner = useCallback(async () => {
    if (isAdhoc || !visitId || !studyScheduleId || !organizationId) return null;
    try {
      const visitData = await fetchVisitPlannerDetails({
        studyScheduleId,
        visitId,
        organizationId,
        createdBy,
        plannerFallback: visitPlannerSnapshot,
      });
      const mergedPlanner = mergePlannerRows(
        visitData.planner,
        visitPlannerSnapshot,
      );
      setPlanner(mergedPlanner);
      setCurrencySymbol(visitData.currencySymbol || "£");
      return { ...visitData, planner: mergedPlanner };
    } catch {
      if (visitPlannerSnapshot) {
        setPlanner(visitPlannerSnapshot);
        return { planner: visitPlannerSnapshot, currencySymbol };
      }
      return null;
    }
  }, [
    createdBy,
    currencySymbol,
    isAdhoc,
    organizationId,
    studyScheduleId,
    visitId,
    visitPlannerSnapshot,
  ]);

  const notify = (message, type = "success") => onNotify?.({ message, type });
  const onSaved = (message) => {
    notify(message, "success");
    onReadinessRefresh?.();
  };
  const onError = (message) => notify(message || "Could not save.", "error");

  useEffect(() => {
    if (!studyScheduleId || !organizationId) {
      setPlanner(null);
      setAdhocPayment("");
      setAdhocVisitCost("");
      return;
    }

    if (isAdhoc) {
      let cancelled = false;
      (async () => {
        try {
          const data = await fetchAdhocSetup({ ...ctx, visitId: null });
          if (cancelled) return;
          setAdhocPayment(data.payment ?? "");
          setAdhocVisitCost(data.visitCost ?? "");
          setCurrencySymbol(data.currencySymbol || "£");
        } catch {
          if (!cancelled) {
            setAdhocPayment("");
            setAdhocVisitCost("");
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (!visitId) {
      setPlanner(null);
      return;
    }

    if (visitPlannerSnapshot) {
      setPlanner(visitPlannerSnapshot);
    }

    let cancelled = false;
    (async () => {
      try {
        const visitData = await reloadPlanner();
        if (cancelled || !visitData) return;
        if (setupFlags?.eCommunication) {
          const commData = await fetchEcommTemplates({
            ...ctx,
            visitId,
            studyScheduleId,
            organizationId,
            createdBy,
            groupId,
          });
          if (cancelled) return;
          setCurrencySymbol(
            visitData.currencySymbol || commData.currencySymbol || "£",
          );
        }
      } catch {
        if (!cancelled) {
          if (visitPlannerSnapshot) {
            setPlanner(visitPlannerSnapshot);
          } else {
            setPlanner(null);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    visitId,
    studyScheduleId,
    organizationId,
    createdBy,
    groupId,
    isAdhoc,
    reloadPlanner,
    visitPlannerSnapshot,
    setupFlags?.eCommunication,
  ]);

  const enrichedCtx = {
    ...ctx,
    planner,
    currencySymbol,
    adhocPayment,
    adhocVisitCost,
  };

  if (!visit && layout !== "workspace") return null;

  const needsApply = !isAdhoc && !visitId;
  const showPayment = !!setupFlags?.visitPayment;
  const showCost = !!setupFlags?.visitCost;
  const isColumnLayout = layout === "columns";
  const isWorkspaceLayout = layout === "workspace";

  const pendingNote = needsApply ? (
    <p className="gvisit-legacy-section__note gvisit-optional-pending">
      This visit is still being saved. Wait a moment, or use <strong>Add visit</strong> to
      create it in the database.
    </p>
  ) : null;

  const archived = !!isStudyArchived || !!readOnly;
  const live = !!isScheduleLive && !archived;
  const plannerSlotSaved = readPlannerSlotFields(planner).hasSaved;
  const financial = readPlannerFinancialFields(planner);
  const paymentSaved = isAdhoc
    ? String(adhocPayment ?? "").trim() !== ""
    : financial.hasSavedPayment;
  const costSaved = isAdhoc
    ? String(adhocVisitCost ?? "").trim() !== ""
    : financial.hasSavedVisitCost ||
      (isScreening && screeningRandomisationTogether && financial.hasSavedRandomCost);
  const plannerReadOnly = freezeSavedWhenLive({
    archived,
    live,
    saved: plannerSlotSaved,
  });
  const paymentReadOnly = freezeSavedWhenLive({
    archived,
    live,
    saved: paymentSaved,
  });
  const costReadOnly = freezeSavedWhenLive({
    archived,
    live,
    saved: costSaved,
  });
  const commReadOnly = archived;
  const docsReadOnly = archived;
  const docsAllowDelete = !live && !archived;

  const plannerSetupSection = !isAdhoc ? (
    <VisitPlannerSetupSection
      ctx={ctx}
      planner={planner}
      visitName={visitName}
      needsApply={needsApply}
      onSaved={onSaved}
      onAuditEvent={onAuditEvent}
      onError={onError}
      onReload={reloadPlanner}
      onPlannerPatch={patchPlanner}
      readOnly={plannerReadOnly}
    />
  ) : null;

  const paymentToggle = getSetupToggle("visitPayment");
  const costToggle = getSetupToggle("visitCost");
  const commToggle = getSetupToggle("eCommunication");
  const worksheetToggle = getSetupToggle("studyWorksheet");

  const paymentSection = showPayment ? (
    <VisitPaymentSection
      ctx={enrichedCtx}
      isAdhoc={isAdhoc}
      showPayment={showPayment}
      visitName={visitName}
      onSaved={onSaved}
      onAuditEvent={onAuditEvent}
      onError={onError}
      onReload={reloadPlanner}
      onPlannerPatch={patchPlanner}
      readOnly={paymentReadOnly}
    />
  ) : visit ? (
    <FeatureOffNote
      title="Payment setup"
      impact={paymentToggle?.offImpact}
    />
  ) : null;

  const costSection = showCost ? (
    <VisitCostSection
      ctx={enrichedCtx}
      isScreening={isScreening}
      isAdhoc={isAdhoc}
      showRandomCost={isScreening && screeningRandomisationTogether}
      showCost={showCost}
      visitName={visitName}
      onSaved={onSaved}
      onAuditEvent={onAuditEvent}
      onError={onError}
      onReload={reloadPlanner}
      onPlannerPatch={patchPlanner}
      readOnly={costReadOnly}
    />
  ) : visit ? (
    <FeatureOffNote
      title="Cost setup"
      impact={costToggle?.offImpact}
    />
  ) : null;

  const isAccordionLayout = isColumnLayout || isWorkspaceLayout;
  const commSection = setupFlags?.eCommunication ? (
    <EcommunicationSection
      ctx={ctx}
      isAdhoc={isAdhoc}
      compact={compact}
      visitName={visitName}
      onSaved={onSaved}
      onAuditEvent={onAuditEvent}
      onError={onError}
      readOnly={commReadOnly}
      showInlineExpand={!isAccordionLayout}
      expandRef={commExpandRef}
    />
  ) : null;

  const commExpandButton = setupFlags?.eCommunication ? (
    <button
      type="button"
      className="btn btn-outline-secondary btn-sm gvisit-comm-expand-btn gvisit-comm-expand-btn--tab"
      title="Open in larger view"
      aria-label="Expand communication"
      onClick={(event) => {
        event.stopPropagation();
        commExpandRef.current?.open();
      }}
    >
      <i className="fas fa-expand" aria-hidden="true" />
    </button>
  ) : null;

  const worksheetSection = setupFlags?.studyWorksheet ? (
    <WorksheetSection
      ctx={ctx}
      isAdhoc={isAdhoc}
      compact={compact}
      visitName={visitName}
      onSaved={onSaved}
      onAuditEvent={onAuditEvent}
      onPromptAuditedVisitAction={onPromptAuditedVisitAction}
      onError={onError}
      readOnly={docsReadOnly}
      allowDelete={docsAllowDelete}
    />
  ) : null;

  const hasPlannerSections = !!(
    timePointSlot ||
    plannerSetupSection ||
    paymentSection ||
    costSection
  );

  const plannerBody = (
    <div className="gvisit-setup-row">
      {timePointSlot}
      {plannerSetupSection}
      {paymentSection}
      {costSection}
      {!visit && isWorkspaceLayout ? (
        <p className="gvisit-workspace__empty gvisit-setup-row__empty">Select a visit from the list.</p>
      ) : null}
      {visit && !hasPlannerSections ? (
        <p className="gvisit-workspace__empty gvisit-setup-row__empty">No planner options enabled.</p>
      ) : null}
    </div>
  );

  const setupAccordions = (
    <VisitSetupAccordionGroup
      plannerBody={plannerBody}
      commSection={commSection}
      commExpandButton={commExpandButton}
      commToggle={commToggle}
      worksheetSection={worksheetSection}
      worksheetToggle={worksheetToggle}
    />
  );

  if (isWorkspaceLayout) {
    return (
      <div className="gvisit-workspace">
        {pendingNote}
        <div className="gvisit-workspace__grid">
          <div className="gvisit-workspace__col gvisit-workspace__col--rail">{visitRailSlot}</div>
          <div className="gvisit-workspace__col gvisit-workspace__col--stack">
            {setupAccordions}
          </div>
        </div>
      </div>
    );
  }

  if (isColumnLayout) {
    return (
      <div className="gvisit-detail__columns-wrap">
        {pendingNote}
        <div className="gvisit-detail__columns gvisit-detail__columns--stack">
          {setupAccordions}
        </div>
      </div>
    );
  }

  return (
    <>
      {pendingNote}
      {paymentSection}
      {costSection}
      {commSection}
      {worksheetSection}
    </>
  );
}
